import type { Vec2 } from '../../geo/types';
import type { LifePose, LifeRoute } from './types';
import { lifePoseAt } from './routes';
import { distance } from './streetGraph';
import { turnHeading } from './heading';
import { createCompany, visible } from './castCompany';
import {
  approach,
  bearing,
  bodyRadius,
  offsetAt,
  PACE,
  shifted,
  toward,
  turnBetween,
  type Walker,
} from './castPace';

export { PACE } from './castPace';

/** Local passing and yielding changes progress, not the public itinerary or its door destinations. */
export function createCrowdMotion(
  routes: readonly LifeRoute[],
  canStep: (from: Vec2, to: Vec2, radius: number) => boolean,
  /* Whether a route's citizen is in the street at this hour; one who is not is in no one's way. */
  out: (route: LifeRoute) => boolean = () => true
) {
  const walkers = new Map(
    routes.map((route) => [
      route,
      {
        clock: 0,
        offset: [0, 0],
        pose: lifePoseAt(route, 0),
        walked: route.phase * route.speed,
        speed: 0,
        gait: 0,
        side: 0,
        aside: 0,
        choice: { kind: 'go', side: 0 },
        decided: 0,
        wary: false,
        thwarted: 0,
        squeezing: 0,
      } satisfies Walker,
    ])
  );
  let lastTime = 0;
  let held: LifeRoute | null = null;
  let heldHeading: number | null = null;
  const { occupied, crowded, threatened, headway } = createCompany(routes, walkers, out);
  /* The seconds of walking a route has before it, as far as it needs to know to slow in time. */
  const walkingLeft = (route: LifeRoute, walker: Walker, within: number): number => {
    const parts = 5;
    for (let i = 1; i <= parts; i++) {
      const then = lifePoseAt(route, walker.clock + (within * i) / parts);
      if (!then.walking || !visible(then)) return (within * (i - 1)) / parts;
    }
    return within;
  };
  const stood = (walker: Walker, dt: number): number => {
    walker.speed = 0;
    walker.gait = toward(walker.gait, 0, dt / PACE.gaitS);
    return walker.gait;
  };
  function step(route: LifeRoute, walker: Walker, dt: number): void {
    if (route === held) {
      walker.pose = {
        ...walker.pose,
        heading: turnHeading(walker.pose.heading, heldHeading ?? walker.pose.heading, dt),
        walking: false,
        gaitWeight: stood(walker, dt),
        action: 'wait',
      };
      return;
    }
    const before = walker.pose;
    const ahead = lifePoseAt(route, walker.clock + dt);
    if (!visible(ahead) || !visible(before)) {
      walker.clock += dt;
      walker.offset = [0, 0];
      /* One who comes out of a door comes out facing the way it is going. */
      const onward = lifePoseAt(route, walker.clock + PACE.lookS);
      walker.pose =
        visible(ahead) && ahead.walking && distance(ahead.position, onward.position) > 0.05
          ? { ...ahead, heading: bearing(ahead.position, onward.position) }
          : ahead;
      walker.gait = 0;
      walker.speed = 0;
      return;
    }
    const here = lifePoseAt(route, walker.clock);
    /* What its itinerary asks of it here, in metres a second: a doorway is slower than a street. */
    const asked = distance(here.position, ahead.position) / dt;
    if (!here.walking || !ahead.walking || asked < 1e-6) {
      walker.clock += dt;
      /* It arrives a last short step from where it stops: it finishes that step as it settles,
         so it leaves again from its own line and not from beside it. */
      const short = distance(before.position, ahead.position);
      const settled =
        short < PACE.settleM
          ? approach(before.position, ahead.position, Math.max(walker.speed, PACE.settleM) * dt)
          : before.position;
      const position = occupied(route, settled, before.position) ? before.position : settled;
      walker.offset = [position[0] - ahead.position[0], position[1] - ahead.position[1]];
      walker.pose = {
        ...ahead,
        position,
        heading: turnHeading(before.heading, ahead.heading, dt),
        walking: false,
        gaitWeight: stood(walker, dt),
      };
      return;
    }
    const canPass = !ahead.door && route.role !== 'horse';
    const nearDoor = lifePoseAt(route, walker.clock + PACE.doorS).door;
    const enough = PACE.thwartedS;
    /* It goes through only one who is as stuck as it is. One who stands there because its day
       has it stand there will move on, and is waited for. */
    if (
      walker.thwarted >= enough &&
      routes.some((other) => {
        if (other === route || !out(other)) return false;
        const theirs = walkers.get(other)!;
        return (
          visible(theirs.pose) &&
          distance(theirs.pose.position, before.position) < PACE.standoffM &&
          (theirs.thwarted >= enough / 2 || theirs.squeezing > 0)
        );
      })
    ) {
      walker.squeezing = PACE.squeezeS;
      walker.thwarted = 0;
    }
    walker.squeezing = Math.max(0, walker.squeezing - dt);
    const squeezing = walker.squeezing > 0;
    // One who could not go on when it last chose has someone in its way, whatever its
    // itinerary says of where they will both be.
    const threat =
      canPass &&
      !nearDoor &&
      !squeezing &&
      (walker.choice.kind !== 'go' || threatened(route, walker));
    walker.aside = threat ? PACE.asideS : nearDoor ? 0 : Math.max(0, walker.aside - dt);
    if (walker.aside === 0) walker.side = 0;
    // Both opposing walkers prefer their own right; fallback left is checked against solid ground.
    const sides = threat
      ? [...new Set([walker.side || 0.7, 0.7, -0.7, 1.15, -1.15, 0])]
      : [walker.side];
    /* It slows for the end of its walk, and for a turn too sharp to be walked round. */
    const sight = lifePoseAt(route, walker.clock + PACE.lookS);
    const slowing = Math.max(
      PACE.arriving,
      Math.min(
        walkingLeft(route, walker, PACE.stopS) / PACE.stopS,
        visible(sight) && sight.walking
          ? (PACE.standTurn - turnBetween(here.heading, sight.heading)) /
              (PACE.standTurn - PACE.freeTurn)
          : 1
      )
    );
    const gather = route.speed / PACE.rampS;
    interface Step {
      position: Vec2;
      offset: Vec2;
      side: number;
      speed: number;
      advance: number;
      at: LifePose;
      way: number;
    }
    /* A step on along its way, on the side asked for. */
    const go = (desired: number, choosing = false): Step | null => {
      const lateral = distance(walker.offset, shifted([0, 0], ahead.heading, desired));
      const whole = approach(
        walker.offset,
        shifted([0, 0], ahead.heading, desired),
        Math.min(lateral, PACE.sideways * asked * dt)
      );
      const way = bearing(before.position, offsetAt(ahead.position, whole));
      const turning = Math.max(
        0,
        Math.min(
          1,
          (PACE.standTurn - turnBetween(before.heading, way)) / (PACE.standTurn - PACE.freeTurn)
        )
      );
      /* It slows behind whoever is before it. Behind one who walks it keeps their pace; one who
         stands there has taken this way, and it must find another. */
      const inFront = squeezing
        ? { pace: Infinity, standing: false }
        : headway(route, before.position, way, gather);
      if (inFront.standing && inFront.pace < PACE.crawl) return null;
      const wanted = Math.min(asked * Math.min(slowing, turning), inFront.pace);
      const speed = toward(
        walker.speed,
        wanted,
        (wanted < walker.speed ? gather * 2 : gather) * dt
      );
      const reach = speed * dt;
      const sideways = Math.min(lateral, PACE.sideways * reach);
      const forward = Math.sqrt(reach * reach - sideways * sideways);
      let advance = forward / asked;
      let at = lifePoseAt(route, walker.clock + advance);
      /* Its itinerary runs slower round a corner and through a door than where the pace was
         asked of it; the second reckoning covers the ground meant, not the seconds. */
      const covered = distance(here.position, at.position);
      if (covered > 1e-6 && Math.abs(covered - forward) > 1e-4) {
        advance *= forward / covered;
        at = lifePoseAt(route, walker.clock + advance);
      }
      /* One who has stepped aside swings wide or turns short on a bend. It takes the step its
         pace allows towards where that puts it, and no more. */
      const meant = offsetAt(
        at.position,
        approach(walker.offset, shifted([0, 0], at.heading, desired), sideways)
      );
      const position = approach(before.position, meant, reach);
      const offset: Vec2 = [position[0] - at.position[0], position[1] - at.position[1]];
      if (!squeezing && occupied(route, position, before.position)) return null;
      /* A way is chosen only if there is room to walk it: one who took a way with room for a
         single step took that step, stood, and took it again. */
      if (
        choosing &&
        !squeezing &&
        occupied(
          route,
          [
            before.position[0] + Math.cos(way) * PACE.roomM,
            before.position[1] + Math.sin(way) * PACE.roomM,
          ],
          before.position
        )
      )
        return null;
      /* Coming back to its own cleared line needs no leave; going further from it does. The
         ground is asked about a step of one length whatever its pace: asked about the step
         itself, a slow walker was let in where a quick one was not, and crept there. */
      if (
        distance(offset, [0, 0]) > distance(walker.offset, [0, 0]) + 1e-9 &&
        (desired !== 0 || distance(offset, [0, 0]) > PACE.onLineM) &&
        !canStep(
          before.position,
          approach(before.position, meant, Math.max(reach, PACE.probeM)),
          bodyRadius(route)
        )
      )
        return null;
      return { position, offset, side: desired, speed, advance, at, way };
    };
    /* A step to the side asked for, or back to its own line, without going on. */
    const shift = (desired: number, choosing = false): Step | null => {
      const speed = toward(walker.speed, route.speed * PACE.sideways, gather * dt);
      const offset = approach(walker.offset, shifted([0, 0], here.heading, desired), speed * dt);
      const position = offsetAt(here.position, offset);
      if (distance(position, before.position) < 0.0001) return null;
      if (occupied(route, position, before.position)) return null;
      const target = offsetAt(here.position, shifted([0, 0], here.heading, desired));
      if (
        choosing &&
        occupied(route, approach(before.position, target, PACE.roomM), before.position)
      )
        return null;
      /* It steps aside to get on, and only where it could then get on: one who stepped aside to
         a place as shut as the one it left stepped back again, and so on all day. */
      if (
        choosing &&
        [PACE.roomM, PACE.roomM * 2.5].some((on) =>
          crowded(route, [
            target[0] + Math.cos(here.heading) * on,
            target[1] + Math.sin(here.heading) * on,
          ])
        )
      )
        return null;
      if (
        !canStep(
          before.position,
          approach(before.position, target, Math.max(speed * dt, PACE.probeM)),
          bodyRadius(route)
        )
      )
        return null;
      return {
        position,
        offset,
        side: desired,
        speed,
        advance: 0,
        at: before,
        way: before.heading,
      };
    };
    /* It keeps to what it chose until that time is up, standing if it can no longer be done.
       Someone newly in its way is reason to choose again at once. */
    walker.decided = threat && !walker.wary ? 0 : Math.max(0, walker.decided - dt);
    walker.wary = threat;
    let accepted: Step | null = null;
    if (walker.decided > 0) {
      const { kind, side } = walker.choice;
      if (kind === 'go' && sides.includes(side)) accepted = go(side);
      else if (kind === 'shift') accepted = shift(side);
      else if (kind === 'go') walker.decided = 0;
      // What it chose can no longer be done: it stands until it chooses again.
      if (!accepted && walker.decided > 0) walker.choice = { kind: 'stand', side };
    }
    if (!accepted && walker.decided === 0) {
      walker.decided = PACE.decideS;
      walker.choice = { kind: 'stand', side: walker.side };
      for (const desired of sides) {
        accepted = go(desired, true);
        if (!accepted) continue;
        walker.choice = { kind: 'go', side: desired };
        break;
      }
      /*
       * Returning toward the path before another pass avoids cycling between two blocked sides.
       */
      for (const desired of accepted || !canPass ? [] : [0, ...sides.filter((side) => side)]) {
        accepted = shift(desired, true);
        if (!accepted) continue;
        walker.choice = { kind: 'shift', side: desired };
        break;
      }
    }
    if (!accepted) {
      /* Someone stands where it was going. It has come near enough, and its day goes on from
         where it is. */
      if (walkingLeft(route, walker, PACE.nearS) < PACE.nearS) {
        walker.clock += dt;
        const now = lifePoseAt(route, walker.clock).position;
        walker.offset = [before.position[0] - now[0], before.position[1] - now[1]];
      } else walker.thwarted += dt;
      walker.pose = { ...before, walking: false, gaitWeight: stood(walker, dt) };
      return;
    }
    walker.thwarted =
      accepted.advance < dt * PACE.thwarting
        ? walker.thwarted + dt
        : Math.max(0, walker.thwarted - 2 * dt);
    walker.clock += accepted.advance;
    walker.side = accepted.side;
    walker.offset = accepted.offset;
    const travelled = distance(before.position, accepted.position);
    walker.walked += travelled;
    walker.speed = accepted.speed;
    // By its pace and not by the frame's step, which at a high frame rate is always small.
    const moving = dt > 0 && travelled / dt > PACE.still;
    const aim = offsetAt(
      sight.position,
      approach(
        accepted.offset,
        shifted([0, 0], sight.heading, accepted.side),
        PACE.sideways * accepted.speed * PACE.lookS
      )
    );
    /* It faces the way it steps, which is also what its pace is reckoned by: one that faced
       where it had just come from instead turned back and forth on the spot. Walking, it leans
       into a bend by no more than costs it no pace. */
    const going = accepted.way;
    const leaning =
      moving && visible(sight) && distance(accepted.position, aim) > 0.2
        ? Math.atan2(
            Math.sin(bearing(accepted.position, aim) - going),
            Math.cos(bearing(accepted.position, aim) - going)
          )
        : 0;
    const facing = going + Math.max(-PACE.lean, Math.min(PACE.lean, leaning));
    walker.gait = toward(
      walker.gait,
      moving ? Math.min(1, accepted.speed / (route.speed * PACE.striding)) : 0,
      dt / PACE.gaitS
    );
    walker.pose = {
      ...(accepted.advance ? accepted.at : before),
      position: accepted.position,
      heading: turnHeading(before.heading, facing, dt),
      walking: moving,
      gaitWeight: walker.gait,
    };
  }
  return {
    hold(route: LifeRoute | null, visitor?: Vec2): void {
      held = route;
      heldHeading = null;
      if (!route || !visitor) return;
      const walker = walkers.get(route)!;
      heldHeading = Math.atan2(
        visitor[1] - walker.pose.position[1],
        visitor[0] - walker.pose.position[0]
      );
    },
    update(time: number): ReadonlyMap<LifeRoute, LifePose> {
      const dt = Math.max(0, Math.min(0.1, time - lastTime));
      lastTime = time;
      if (dt) for (const route of routes) step(route, walkers.get(route)!, dt);
      return new Map(routes.map((route) => [route, walkers.get(route)!.pose]));
    },
    distance(route: LifeRoute): number {
      return walkers.get(route)!.walked;
    },
    speed(route: LifeRoute): number {
      return walkers.get(route)!.speed;
    },
  };
}
